import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiRequest } from '@/lib/api-client'

const connectorSchema = z.object({
  id: z.string(),
  label: z.string(),
  host: z.string(),
  port: z.number(),
  tls: z.boolean(),
  username: z.string(),
  folder: z.string(),
  active: z.boolean(),
  last_synced_at: z.string().nullable(),
  last_uid: z.number().nullable(),
  created_at: z.string(),
})
type Connector = z.infer<typeof connectorSchema>

const connectorsResponseSchema = z.object({ connectors: z.array(connectorSchema) })
const syncResponseSchema = z.object({
  fetched: z.number(),
  saved: z.number(),
  lastUid: z.number(),
  errors: z.array(z.string()),
})

export function SettingsConnectorsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    label: 'E-Mail-Verbindung',
    host: '',
    port: 993,
    tls: true,
    username: '',
    password: '',
    folder: 'INBOX',
  })

  const connectors = useQuery({
    queryKey: ['connectors'],
    queryFn: () => apiRequest({ path: '/connectors', schema: connectorsResponseSchema }),
  })

  const create = useMutation({
    mutationFn: () => apiRequest({
      method: 'POST',
      path: '/connectors/email',
      body: form,
      schema: connectorSchema,
    }),
    onSuccess: () => {
      toast.success('Konnektor gespeichert')
      setForm((value) => ({ ...value, password: '' }))
      void qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Konnektor konnte nicht gespeichert werden'),
  })

  const sync = useMutation({
    mutationFn: (id: string) => apiRequest({
      method: 'POST',
      path: `/connectors/${id}/sync`,
      schema: syncResponseSchema,
    }),
    onSuccess: (result) => {
      toast.success(`${result.saved} E-Mails importiert`)
      void qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Sync fehlgeschlagen'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiRequest({
      method: 'DELETE',
      path: `/connectors/${id}`,
      schema: z.null(),
    }),
    onSuccess: () => {
      toast.success('Konnektor entfernt')
      void qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Konnektor konnte nicht entfernt werden'),
  })

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <div>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight">Konnektoren</h1>
        <p className="mt-1 text-sm text-muted-foreground">E-Mail-Quellen in den Wissensspeicher einlesen.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>IMAP-Verbindung</CardTitle>
          <CardDescription>MindKeep speichert nur eine verschlüsselte Verbindung und importiert E-Mails als Markdown.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Label">
            <Input value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} />
          </Field>
          <Field label="Host">
            <Input placeholder="imap.example.com" value={form.host} onChange={(event) => setForm({ ...form, host: event.target.value })} />
          </Field>
          <Field label="Port">
            <Input type="number" value={form.port} onChange={(event) => setForm({ ...form, port: Number(event.target.value) })} />
          </Field>
          <Field label="Ordner">
            <Input value={form.folder} onChange={(event) => setForm({ ...form, folder: event.target.value })} />
          </Field>
          <Field label="Benutzername">
            <Input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
          </Field>
          <Field label="Passwort">
            <Input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-11 w-11 shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-4 min-[761px]:w-4"
              checked={form.tls}
              onChange={(event) => setForm({ ...form, tls: event.target.checked })}
            />
            TLS verwenden
          </label>
          <div className="flex justify-end">
            <Button disabled={create.isPending || !form.host || !form.username || !form.password} onClick={() => create.mutate()}>
              Verbindung speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {(connectors.data?.connectors ?? []).map((connector) => (
          <ConnectorRow
            key={connector.id}
            connector={connector}
            syncing={sync.isPending}
            deleting={remove.isPending}
            onSync={() => sync.mutate(connector.id)}
            onDelete={() => remove.mutate(connector.id)}
          />
        ))}
        {connectors.data?.connectors.length === 0 && (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">Noch keine Konnektoren eingerichtet.</p>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

function ConnectorRow({
  connector,
  syncing,
  deleting,
  onSync,
  onDelete,
}: {
  connector: Connector
  syncing: boolean
  deleting: boolean
  onSync: () => void
  onDelete: () => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border p-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="break-words text-sm font-medium">{connector.label}</p>
          <p className="break-all text-xs text-muted-foreground">
            {connector.username} · {connector.host}:{connector.port} · {connector.folder}
          </p>
          <p className="text-xs text-muted-foreground">
            Letzter Sync: {connector.last_synced_at ? new Date(connector.last_synced_at).toLocaleString('de-CH') : 'noch nie'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={syncing} onClick={onSync}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Sync
        </Button>
        <Button variant="ghost" size="icon" disabled={deleting} onClick={onDelete} title="Entfernen" aria-label={`Konnektor ${connector.label} entfernen`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
